import { TextTooltip } from '@components'
import { NOCFO_PLAN_OPTIONS } from '@constants'
import { getSettingsPageUrl } from '@root/constants'
import { IBusiness, useTrialLimits } from '@query'
import { getOptionLabelByValue } from '@utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaExclamationTriangle } from 'react-icons/fa'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'

interface TrialTrackerProps {
  slug: string
  business: IBusiness | null
  isNarrow: boolean
}

export const TrialTracker: React.FC<TrialTrackerProps> = ({ slug, business, isNarrow }) => {
  const [t] = useTranslation()
  const history = useHistory()
  const isTrialing = business?.stripe_plan_status === 'trialing'

  const { data: trialLimits } = useTrialLimits({
    enabled: isTrialing
  })

  const trialPlanName = getOptionLabelByValue(NOCFO_PLAN_OPTIONS, business?.subscription_plan, {
    t,
    fallback: '',
    fallbackToValue: true
  })
  const documentUsage = trialLimits?.document_usage
  const hasTrialLimit = documentUsage?.limit != null
  const hasTrialDaysRemaining = trialLimits?.trial_days_remaining !== null
  const showTrialTracker =
    isTrialing && hasTrialLimit && trialLimits?.is_trialing !== false && hasTrialDaysRemaining
  const trialLimitValue = documentUsage?.limit ?? 0
  const trialUsedValue = documentUsage?.used ?? 0
  const trialRemainingValue = Math.max(
    documentUsage?.remaining ?? trialLimitValue - trialUsedValue,
    0
  )
  const paygInvoiceUsage = trialLimits?.payg_invoice_usage
  const hasPaygInvoiceLimit = paygInvoiceUsage?.is_enabled && paygInvoiceUsage.limit != null
  const paygInvoiceLimitValue = paygInvoiceUsage?.limit ?? 0
  const paygInvoiceUsedValue = paygInvoiceUsage?.used ?? 0
  const paygInvoiceRemainingValue =
    paygInvoiceUsage?.remaining ?? Math.max(paygInvoiceLimitValue - paygInvoiceUsedValue, 0)
  const paygInvoiceLimitReached = hasPaygInvoiceLimit && paygInvoiceRemainingValue <= 0
  const trialLimitReached =
    showTrialTracker && (trialRemainingValue <= 0 || paygInvoiceLimitReached)
  const trialDaysRemaining = trialLimits?.trial_days_remaining ?? 0

  const planSettingsUrl = getSettingsPageUrl(slug, { page: 'plan' })

  if (!showTrialTracker) return null

  return (
    <TrialTrackerWrapper>
      <TextTooltip
        tooltip={t(
          hasPaygInvoiceLimit
            ? 'mainMenu.trialTracker.tooltipWithInvoices'
            : 'mainMenu.trialTracker.tooltip',
          {
            used: trialUsedValue,
            limit: trialLimitValue,
            remaining: trialRemainingValue,
            invoiceUsed: paygInvoiceUsedValue,
            invoiceLimit: paygInvoiceLimitValue,
            invoiceRemaining: paygInvoiceRemainingValue,
            planName: trialPlanName,
            trialDaysRemaining
          }
        )}
        placement="top-start"
      >
        <TrialTrackerButton
          type="button"
          $isNarrow={isNarrow}
          $isWarning={trialLimitReached}
          onClick={() => history.push(planSettingsUrl)}
        >
          {trialLimitReached && <TrialAlertIcon />}
          <TrialTrackerLabel>
            {trialLimitReached
              ? t('mainMenu.trialTracker.limitReachedLabel')
              : t('mainMenu.trialTracker.label', { trialDaysRemaining })}
          </TrialTrackerLabel>
        </TrialTrackerButton>
      </TextTooltip>
    </TrialTrackerWrapper>
  )
}

export const TrialTrackerWrapper = styled.div`
  width: 100%;

  > * {
    display: block;
    width: 100%;
  }
`

const TrialTrackerButton = styled.button<{ $isNarrow: boolean; $isWarning: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  border: 2px solid
    ${({ theme, $isWarning }) =>
      $isWarning ? `${theme.colors.warning}cc` : `${theme.colors.primary}66`};
  border-radius: 0.75rem;
  background: ${({ theme, $isWarning }) =>
    $isWarning ? `${theme.colors.warning}22` : `${theme.colors.primary}22`};
  color: ${({ theme, $isWarning }) =>
    $isWarning ? theme.colors.warning : theme.colors.primary} !important;
  padding: ${({ theme }) => theme.spacing.xs}rem;
  cursor: pointer;
  width: 100%;
  justify-content: ${({ $isNarrow }) => ($isNarrow ? 'center' : 'flex-start')};
  text-align: left;
  font-weight: bold;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;

  &:hover {
    border-color: ${({ theme, $isWarning }) =>
      $isWarning ? theme.colors.warning : theme.colors.primary};
  }
`

const TrialTrackerLabel = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TrialAlertIcon = styled(FaExclamationTriangle)`
  color: ${({ theme }) => theme.colors.warning};
  width: 0.9rem;
  height: 0.9rem;
  flex-shrink: 0;
`
